import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FileText,
  Import,
  Briefcase,
  HelpCircle,
  PlusCircle,
  Info,
  File,
  LogOut,
  BookOpen,
  House,
  Lightbulb,
  Hotel,
  Building2,
  MessageCircle,
  Users,
  BriefcaseBusiness,
  Clipboard,
  ChartLine,
  Copy,
  SquareArrowUpLeft,
  BarChartBig,
  Globe,
  Link,
  Wrench,
  Award,
  LayoutDashboardIcon,
  Network 
} from 'lucide-react';
import { cn } from '../utils/cn';
import { Modal, Button, Tooltip } from './ui';
import {
  StyledLogoutConfirmation,
  StyledConfirmationText,
  StyledActionButtons,
  StyledConfirmationContent,
} from './Sidebar/LogoutConfirmation.styled';
import Logo from '../assets/logo.png';
import navigationData from '../utils/navigationData.json';
import { useAuth } from '../contexts/AuthContext';
import BoostStarIcon from '../assets/icons/BoostStarIcon';
import { shouldShowNavItem } from '../utils/tabDisplayMapping';
import HotelBrandIcon from '../assets/icons/HotelBrandIcon';
// Icon mapping for dynamic icon rendering
const iconMap = {
  FileText,
  Import,
  Briefcase,
  HelpCircle,
  PlusCircle,
  Info,
  File,
  LogOut,
  BookOpen,
  House,
  Lightbulb,
  Hotel,
  Building2,
  BarChartBig,
  Users,
  BriefcaseBusiness,
  Star: BoostStarIcon,
  MessageCircle,
  Globe,
  Clipboard,
  ChartLine,
  Copy,
  SquareArrowUpLeft,
  Link,
  Wrench,
  Award,
  LayoutDashboardIcon,
  HotelBrand: HotelBrandIcon,
  Network
} as const;

type IconName = keyof typeof iconMap;

// Styled component variants
const sidebarVariants = {
  default: 'sidebar-default',
  dark: 'sidebar-dark',
  minimal: 'sidebar-minimal',
} as const;

const sizeVariants = {
  normal: '',
  wide: 'sidebar-wide',
} as const;

const stateVariants = {
  expanded: 'sidebar-expanded',
  collapsed: 'sidebar-collapsed',
} as const;

const logoVariants = {
  expanded: 'sidebar-logo-expanded',
  collapsed: 'sidebar-logo-collapsed',
} as const;

export interface SidebarProps {
  isOpen: boolean;
  variant?: keyof typeof sidebarVariants;
  size?: keyof typeof sizeVariants;
  className?: string;
  mobileOpen?: boolean;
  onLinkClick?: (path: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  variant = 'default',
  size = 'normal',
  className,
  mobileOpen = false,
  onLinkClick,
}) => {
  const { navigation } = navigationData.sidebar;
  const { logout, tabs, tabsLoading, user } = useAuth();

  // Dynamically adjust navigation items based on user role
  const adjustedNavigation = navigation.map(link => {
    // Handle Reporting navigation item dynamically
    if (link.text === 'Reporting') {
      if (user?.role === 'ALO_SUPER') {
        return {
          ...link,
          to: '/reporting',
          icon: 'BarChartBig',
        };
      } else if (user?.role === 'ENTERPRISE_ADMIN') {
        return {
          ...link,
          to: '/travel-managers/reporting',
          icon: 'BarChartBig',
        };
      }
      else if (user?.role === 'BRAND_ADMIN') {
        return {
          ...link,
          to: '/brand-admin/reporting',
          icon: 'BarChartBig',
        }
      }
      else {
        return {
          ...link,
          to: '/403',
          icon: 'BarChartBig',
        };
      }
    }
    return link;
  });

  // Filter navigation items based on user's tabs and role
  const navLinks = adjustedNavigation.filter(link =>
    shouldShowNavItem(link.text, tabs, user?.role)
  );

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const renderIcon = (iconName: string, size: number = 20) => {
    const IconComponent = iconMap[iconName as IconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  const handleLinkClick = (path: string) => {
    onLinkClick?.(path);
  };

  const handleLogout = async () => {
    try {
      // AuthContext.logout() posts to /auth/logout and clears credentials via
      // AuthService, which owns storage cleanup in a finally block. Do not
      // clear localStorage here — duplicating the keys risks them going stale.
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      window.location.href = '/login';
    }
  };

  const handleConfirmLogout = () => {
    handleLogout();
    setIsLogoutModalOpen(false);
  };

  const handleCancelLogout = () => {
    setIsLogoutModalOpen(false);
  };

  if (tabsLoading) {
    return null;
  }

  return (
    <aside
      className={cn(
        'sidebar-base',
        sidebarVariants[variant],
        isOpen ? stateVariants.expanded : stateVariants.collapsed,
        sizeVariants[size],
        mobileOpen && 'sidebar-mobile-open',
        className,
        isOpen && 'h-100vh overflow-y-auto'
      )}
    >
      {/* Logo Section */}
      <div
        className={`sidebar-logo-container ${
          !isOpen && 'sidebar-logo-container-collapsed'
        }`}
      >
        <img
          src={Logo}
          alt="Alō Index"
          className={cn(
            isOpen ? logoVariants.expanded : logoVariants.collapsed
          )}
        />
      </div>

      {/* Navigation Section */}
      <nav
        className="sidebar-nav custom-scrollbar"
        role="navigation"
        aria-label="Main navigation"
      >
        {navLinks.map(link =>
          isOpen ? (
            <NavLink
              key={link.to}
              to={link.to}
              target={link.target || '_self'}
              onClick={() => handleLinkClick(link.to)}
              className={({ isActive }) =>
                cn('sidebar-nav-link', isActive && 'sidebar-nav-link-active')
              }
              aria-label={link.text}
            >
              <div className="sidebar-nav-icon" aria-hidden="true">
                {renderIcon(link.icon)}
              </div>
              {isOpen && <span className="sidebar-nav-text">{link.text}</span>}
            </NavLink>
          ) : (
            <Tooltip
              key={link.to}
              content={link.text}
              position="right"
              iconClassName="!flex"
              icon={
                <NavLink
                  to={link.to}
                  target={link.target || '_self'}
                  onClick={() => handleLinkClick(link.to)}
                  className={({ isActive }) =>
                    cn(
                      'sidebar-nav-link',
                      isActive && 'sidebar-nav-link-active'
                    )
                  }
                  aria-label={link.text}
                >
                  <div className="sidebar-nav-icon" aria-hidden="true">
                    {renderIcon(link.icon)}
                  </div>
                  {isOpen && (
                    <span className="sidebar-nav-text">{link.text}</span>
                  )}
                </NavLink>
              }
            />
          )
        )}
      </nav>

      {/* Help Button - Above Logout */}

      <div
        className="sidebar-logout-container"
        style={{ marginTop: 'auto', paddingBottom: '0.5rem', border: 'none' }}
      >
        {isOpen ? (
          <button
            onClick={() => (window.location.href = 'mailto:help@aloindex.com')}
            className="sidebar-logout-button"
            aria-label="Help & Support"
          >
            <div className="sidebar-nav-icon" aria-hidden="true">
              {renderIcon('MessageCircle')}
            </div>
            {isOpen && <span className="sidebar-nav-text">Help & Support</span>}
          </button>
        ) : (
          <Tooltip
            content="Help & Support"
            position="right"
            iconClassName="!flex"
            icon={
              <button
                onClick={() =>
                  (window.location.href = 'mailto:help@aloindex.com')
                }
                className="sidebar-logout-button"
                aria-label="Help & Support"
              >
                <div className="sidebar-nav-icon" aria-hidden="true">
                  {renderIcon('MessageCircle')}
                </div>
                {isOpen && (
                  <span className="sidebar-nav-text">Help & Support</span>
                )}
              </button>
            }
          />
        )}
      </div>
      {/* Logout Section at Bottom */}
      <div className="sidebar-logout-container">
        {isOpen ? (
          <button
            onClick={() => setIsLogoutModalOpen(true)}
            className="sidebar-logout-button"
            aria-label="Log out"
          >
            <div className="sidebar-nav-icon" aria-hidden="true">
              {renderIcon('LogOut')}
            </div>
            {isOpen && <span className="sidebar-nav-text">Log out</span>}
          </button>
        ) : (
          <Tooltip
            content="Log out"
            position="right"
            icon={
              <button
                onClick={() => setIsLogoutModalOpen(true)}
                className="sidebar-logout-button"
                aria-label="Log out"
              >
                <div className="sidebar-nav-icon" aria-hidden="true">
                  {renderIcon('LogOut')}
                </div>
                {isOpen && <span className="sidebar-nav-text">Log out</span>}
              </button>
            }
          />
        )}
      </div>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={isLogoutModalOpen}
        onClose={handleCancelLogout}
        title="Confirm Log out"
        size="sm"
      >
        <StyledLogoutConfirmation>
          <StyledConfirmationContent>
            <h3>Are you sure you want to log out?</h3>
            <StyledConfirmationText>
              You will be redirected to the log in page and need to sign in
              again.
            </StyledConfirmationText>
          </StyledConfirmationContent>

          <StyledActionButtons>
            <Button variant="outline" onClick={handleCancelLogout}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmLogout}>
              Log out
            </Button>
          </StyledActionButtons>
        </StyledLogoutConfirmation>
      </Modal>
    </aside>
  );
};

export default Sidebar;
